import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, filter, map, ReplaySubject, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { notNil } from 'app/utils/stream-util';

import { socialAppSelectors } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';

export interface PlayerState {
  isPresenting: boolean;
  videoEnabled: boolean;
  micEnabled: boolean;
  remoteMicEnabled: boolean;
  audioEnabled: boolean;
  connectionStatus: ConnectionStatus;
  groupShareActive: boolean;
}

export enum ConnectionStatus {
  DISCONNECTED,
  INTERMITTENT,
  CONNECTED,
  CONNECTING,
  NONE,
}

const INITIAL_STATE: PlayerState = {
  isPresenting: false,
  videoEnabled: true,
  micEnabled: true,
  remoteMicEnabled: true,
  audioEnabled: true,
  connectionStatus: ConnectionStatus.NONE,
  groupShareActive: false,
};
export class PlayerStateMachine {
  public state: PlayerState;
  public stateChange$ = new BehaviorSubject<PlayerState>({ ...INITIAL_STATE });

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(store: Store<AppState>, userId: string) {
    this.state = { ...INITIAL_STATE };
    store
      .select(socialAppSelectors.getSubscriptionStatus)
      .pipe(
        filter(notNil),
        map((sub) => sub[userId]?.state),
        // TODO: are we not updating the SubscriptionState?
        // marginalBadgeVisible is being set innapropriately
        filter((status) => status === SubscriptionState.SUBSCRIBING),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        const { state } = this;
        this.setState({ ...state, connectionStatus: ConnectionStatus.CONNECTING });
      });
    store
      .select(socialAppSelectors.getGroupScreenShareUsers)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((ids) => {
        const { state } = this;
        const isInGroupShare = ids.includes(userId);
        this.setState({
          ...state,
          groupShareActive: isInGroupShare,
        });
      });
  }

  public setState(state: PlayerState): void {
    this.state = state;
    this.stateChange$.next(this.state);
  }

  //Show mic badge if the user is muted
  public get micBadgeVisible(): boolean {
    const { audioEnabled, isPresenting, micEnabled, videoEnabled } = this.state;
    if (isPresenting) {
      return !micEnabled;
    }
    if (videoEnabled) {
      return !audioEnabled;
    }
    return false;
  }

  public get videoOverlayVisible(): boolean {
    const { audioEnabled, connectionStatus, isPresenting, videoEnabled } = this.state;
    if (isPresenting || connectionStatus === ConnectionStatus.DISCONNECTED) {
      return false;
    }
    return !videoEnabled && audioEnabled && !this.groupActivityOverlayVisible;
  }

  public get videoAndMicOverlayVisible(): boolean {
    const { audioEnabled, connectionStatus, isPresenting, videoEnabled } = this.state;
    if (isPresenting || connectionStatus === ConnectionStatus.DISCONNECTED) {
      return false;
    }

    return !videoEnabled && !audioEnabled;
  }

  public get presenterOverlayVisible(): boolean {
    const { connectionStatus, isPresenting } = this.state;
    return isPresenting && connectionStatus !== ConnectionStatus.DISCONNECTED;
  }

  public get disconnectedOverlayVisible(): boolean {
    return this.state.connectionStatus === ConnectionStatus.DISCONNECTED;
  }

  public get groupActivityOverlayVisible(): boolean {
    return this.state.groupShareActive;
  }

  public get connectedBadgeVisible(): boolean {
    return this.state.connectionStatus === ConnectionStatus.CONNECTED;
  }

  public get connectingBadgeVisible(): boolean {
    return this.state.connectionStatus === ConnectionStatus.CONNECTING;
  }

  public get marginalBadgeVisible(): boolean {
    return this.state.connectionStatus === ConnectionStatus.INTERMITTENT;
  }

  /**
   * Indicates that the player's video should be shown
   *
   * This is the inverse of the other video overlay visibilities
   */
  public get videoVisible(): boolean {
    return !(
      this.videoAndMicOverlayVisible ||
      this.videoOverlayVisible ||
      this.videoAndMicOverlayVisible ||
      this.presenterOverlayVisible ||
      this.groupActivityOverlayVisible
    );
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
